/**
 * A small popover anchored under a trigger button, on the *host page*.
 *
 * Unlike the overlay, this never involves the iframe: it is a choice about
 * which flow to open, so opening the embed just to ask would mean loading a
 * cross-origin document and completing a handshake before the patient has
 * decided anything.
 *
 * Same shadow-root treatment as the overlay, and for the same reason — a
 * client's stylesheet (Bootstrap resets, Tailwind preflight, aggressive `ul`
 * rules) would otherwise reach in and wreck it.
 *
 * @author Rajkumar Rathod
 */
import { Z_INDEX } from './constants';

const STYLES = `
:host { all: initial; }
.menu {
  position: fixed;
  min-width: 190px;
  padding: 6px 0;
  margin: 0;
  list-style: none;
  background: #ececec;
  border: 0;
  border-radius: 2px;
  box-shadow: 0 6px 16px -4px rgba(15, 23, 42, 0.22);
  font: 400 14px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #1e293b;
}
.item {
  display: block;
  width: 100%;
  padding: 8px 16px;
  border: 0;
  border-radius: 0;
  background: none;
  font: inherit;
  text-align: start;
  cursor: pointer;
  color: inherit;
}
.item:hover { background: rgba(15, 23, 42, 0.06); }
.item:focus-visible { outline: 2px solid #2563eb; outline-offset: -2px; }
`;

export interface MenuItem {
  label: string;
  onSelect: () => void;
}

/**
 * Anchored menu. One is open at a time; opening another closes the first.
 */
export class AnchoredMenu {
  private host: HTMLDivElement;
  private list: HTMLUListElement;
  private buttons: HTMLButtonElement[] = [];
  private anchor: HTMLElement;
  private disposed = false;
  private onClose: (() => void) | undefined;

  /**
   * `onClose` fires once, whatever ended the menu — the owner's own
   * {@link close} call, an outside click, Escape, a scroll, or choosing an
   * item. It exists because most of those are decided in here: an owner that
   * only learns about the calls it makes itself is left holding a handle to a
   * menu that is no longer on the page.
   */
  constructor(anchor: HTMLElement, items: MenuItem[], onClose?: () => void) {
    this.anchor = anchor;
    this.onClose = onClose;

    this.host = document.createElement('div');
    this.host.setAttribute('data-ehr-widget-menu', '');
    this.host.style.setProperty('position', 'absolute', 'important');
    this.host.style.setProperty('z-index', String(Z_INDEX), 'important');

    const root = this.host.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = STYLES;

    this.list = document.createElement('ul');
    this.list.className = 'menu';
    this.list.setAttribute('role', 'menu');
    this.list.setAttribute('aria-label', 'Appointment options');

    for (const item of items) {
      const li = document.createElement('li');
      li.setAttribute('role', 'none');

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'item';
      button.setAttribute('role', 'menuitem');
      button.textContent = item.label;
      button.addEventListener('click', () => {
        // Close first: the handler opens the overlay, which moves focus, and a
        // menu still in the DOM would fight it for the Escape key.
        this.close();
        item.onSelect();
      });

      this.buttons.push(button);
      li.appendChild(button);
      this.list.appendChild(li);
    }

    root.append(style, this.list);
  }

  open(): void {
    document.body.appendChild(this.host);
    this.position();

    // Announced on the trigger rather than the menu: a screen reader user needs
    // to know the button opens a menu *before* they activate it.
    this.anchor.setAttribute('aria-haspopup', 'menu');
    this.anchor.setAttribute('aria-expanded', 'true');
    this.buttons[0]?.focus({ preventScroll: true });

    // Capture phase, so a host page that stops propagation on its own clicks
    // cannot leave the menu stuck open.
    document.addEventListener('click', this.onDocumentClick, true);
    document.addEventListener('keydown', this.onKeyDown, true);
    // Reposition would need to track the anchor through arbitrary layout; a
    // menu that closes on scroll is both simpler and what most UIs do.
    window.addEventListener('scroll', this.onScrollOrResize, true);
    window.addEventListener('resize', this.onScrollOrResize);
  }

  close(): void {
    if (this.disposed) return;
    this.disposed = true;

    document.removeEventListener('click', this.onDocumentClick, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('scroll', this.onScrollOrResize, true);
    window.removeEventListener('resize', this.onScrollOrResize);

    this.host.remove();
    this.anchor.setAttribute('aria-expanded', 'false');

    // Last, and after `disposed` is set: the callback is where the owner drops
    // its reference, and it must not be able to re-enter this.
    this.onClose?.();
  }

  /**
   * Sit directly under the anchor, flipping above it when there is not enough
   * room below, and clamping so the menu never runs off the right edge.
   */
  private position(): void {
    const rect = this.anchor.getBoundingClientRect();
    const menu = this.list.getBoundingClientRect();
    // The design tucks the menu directly under the button, not floating off it.
    const GAP = 0;

    const below = rect.bottom + GAP;
    const fitsBelow = below + menu.height <= window.innerHeight;
    const top = fitsBelow ? below : Math.max(GAP, rect.top - menu.height - GAP);

    const left = Math.min(
      rect.left,
      Math.max(GAP, window.innerWidth - menu.width - GAP)
    );

    this.list.style.top = `${top}px`;
    this.list.style.left = `${left}px`;
  }

  private readonly onDocumentClick = (event: MouseEvent): void => {
    const target = event.target as Node | null;
    // A click on the anchor is handled by the trigger binder, which toggles.
    if (target && this.anchor.contains(target)) return;
    if (target && this.host.contains(target)) return;
    this.close();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      this.close();
      this.anchor.focus({ preventScroll: true });
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    const active = this.buttons.findIndex((b) => b.matches(':focus'));
    const last = this.buttons.length - 1;

    // Focus may be back on the trigger or gone to the page behind, in which
    // case an arrow key means "get me into the menu" rather than "move".
    const next =
      active === -1
        ? event.key === 'ArrowDown'
          ? 0
          : last
        : event.key === 'ArrowDown'
          ? (active + 1) % this.buttons.length
          : (active - 1 + this.buttons.length) % this.buttons.length;

    this.buttons[next]?.focus({ preventScroll: true });
  };

  private readonly onScrollOrResize = (): void => this.close();
}
